@if (quiz(); as quiz) {
  <odl-dialog
    class="quiz-taking-dialog"
    [label]="
      isInformationTitle()
        ? ('QUESTIONS.INFORMATION' | translate)
        : isStandaloneQuestion()
          ? ('QUESTIONS.QUESTION_TITLE' | translate)
          : ('QUESTIONS.QUIZ_TITLE' | translate) + ': ' + quiz.name
    "
    [open]="true"
    [size]="dialogSize()"
    (odl-close)="close()"
  >
    @switch (phase()) {
      @case ('confirm') {
        <p class="dialog-body-text">{{ 'QUESTIONS.START_CONFIRM_BODY' | translate }}</p>
        <div slot="footer" class="dialog-footer-end">
          <odl-button variant="secondary" (click)="close()">{{ 'CANCEL' | translate }}</odl-button>
          <odl-button variant="primary" (click)="startAttempt()">
            {{ 'QUESTIONS.START' | translate }}
          </odl-button>
        </div>
      }

      @case ('question') {
        @if (!isStandaloneQuestion() || quiz.time) {
          <div class="info-row" [class.info-row--with-timer]="quiz.time">
            @if (!isStandaloneQuestion()) {
              <p class="question-count">
                {{
                  'QUESTIONS.QUESTION_OF'
                    | translate
                      : { current: currentQuestionIndex() + 1, total: quiz.questions.length }
                }}
              </p>
            }
            @if (quiz.time) {
              <div class="timer" [class.timer--warning]="isTimeRunningLow()">
                <odl-icon name="clock"></odl-icon>
                <span class="timer-elapsed">{{ elapsedTimeDisplay() }}</span>
                <span class="timer-total"
                  >/ {{ totalTimeDisplay() }} {{ 'QUESTIONS.MINUTES_SHORT' | translate }}</span
                >
              </div>
            }
          </div>
        }
        @if (currentQuestion(); as question) {
          <div class="question-card">
            <p class="question-text">{{ question.text }}</p>
            @if (question.imageUrl) {
              <img class="question-image" [src]="question.imageUrl" alt="" />
            }
            @if (quiz.materialKind === 'simple') {
              <textarea
                #openEndedAnswerInput
                class="open-ended-answer-input"
                [attr.id]="'answer-' + question.id"
                [attr.name]="'answer-' + question.id"
                [value]="openEndedAnswerText()"
                [attr.placeholder]="'QUESTIONS.WRITE_YOUR_ANSWER_PLACEHOLDER' | translate"
                [attr.aria-label]="question.text"
                (input)="updateOpenEndedAnswerText(openEndedAnswerInput.value)"
              ></textarea>
            } @else {
              <div class="choices">
                @for (choice of currentChoices(); track choice.value) {
                  <label class="choice-row">
                    <input
                      type="radio"
                      [name]="'answer-' + question.id"
                      [checked]="values()[question.id] === choice.value"
                      (change)="selectAnswer(question.id, choice.value)"
                    />
                    <span>{{ choice.text }}</span>
                  </label>
                }
              </div>
            }
          </div>
        }
        @if (attemptsRemaining(); as remaining) {
          <p class="attempts-remaining">
            {{ 'QUESTIONS.ATTEMPTS_REMAINING' | translate: { count: remaining } }}
          </p>
        }
        <div slot="footer" class="dialog-footer dialog-footer--divider">
          <div class="dialog-footer-start">
            @if (currentQuestionIndex() > 0) {
              <odl-button
                variant="text"
                [disabled]="isSubmitting()"
                (click)="goToPreviousQuestion()"
              >
                <odl-icon name="navigate_back" slot="start"></odl-icon>
                {{ 'QUESTIONS.BACK' | translate }}
              </odl-button>
            }
          </div>
          <div class="dialog-footer-end">
            @if (!isLastQuestion()) {
              <odl-button
                variant="primary"
                [disabled]="!hasAnsweredCurrentQuestion()"
                (click)="goToNextQuestion()"
              >
                {{ 'NEXT' | translate }}
                <odl-icon name="navigate_next" slot="end"></odl-icon>
              </odl-button>
            } @else {
              <odl-button
                variant="primary"
                [disabled]="!hasAnsweredCurrentQuestion() || isSubmitting()"
                (click)="saveAnswers()"
              >
                {{ (isSubmitting() ? 'SAVING' : 'SAVE') | translate }}
              </odl-button>
            }
          </div>
        </div>
      }

      @case ('time-expired') {
        <p class="dialog-body-text">{{ 'QUESTIONS.TIME_EXPIRED_BODY' | translate }}</p>
        <div slot="footer" class="dialog-footer-end">
          <odl-button
            variant="primary"
            [disabled]="isSubmitting()"
            (click)="acknowledgeTimeExpired()"
          >
            {{ (isSubmitting() ? 'SAVING' : 'ACCEPT') | translate }}
          </odl-button>
        </div>
      }

      @case ('result') {
        @if (quiz.time && elapsedTimeDisplay(); as elapsed) {
          <div class="timer">
            <odl-icon name="clock"></odl-icon>
            <span class="timer-elapsed">{{ elapsed }}</span>
            <span class="timer-total"
              >/ {{ totalTimeDisplay() }} {{ 'QUESTIONS.MINUTES_SHORT' | translate }}</span
            >
          </div>
        }
        @if (myAnswer()) {
          <div class="question-card question-card--grade">
            <span class="grade-label">{{ 'QUESTIONS.YOUR_GRADE' | translate }}</span>
            @if (isGradePending()) {
              <span class="grade-value grade-value--pending">
                {{ 'QUESTIONS.GRADE_PENDING' | translate }}
              </span>
            } @else {
              <span class="grade-value">{{
                quiz.materialKind === 'simple' ? aiGrade() : gradeOutOfTen()
              }}</span>
            }
          </div>
        }
        @if (canRetry()) {
          @if (attemptsRemaining(); as remaining) {
            <p class="attempts-remaining">
              {{ 'QUESTIONS.ATTEMPTS_REMAINING' | translate: { count: remaining } }}
            </p>
          }
        }
        <div slot="footer" class="dialog-footer-end" [class.dialog-footer--divider]="canRetry()">
          @if (canRetry()) {
            <odl-button variant="secondary" (click)="sendAssignment()">
              {{ 'QUESTIONS.SEND_ASSIGNMENT' | translate }}
            </odl-button>
            <odl-button variant="primary" (click)="tryAgain()">
              {{ 'QUESTIONS.TRY_AGAIN' | translate }}
            </odl-button>
          } @else {
            <odl-button variant="primary" (click)="viewAnswers()">
              {{ 'QUESTIONS.VIEW_ANSWERS' | translate }}
            </odl-button>
          }
        </div>
      }

      @case ('review') {
        @if (!isStandaloneQuestion()) {
          <p class="question-count">
            {{
              'QUESTIONS.QUESTION_OF'
                | translate: { current: reviewIndex() + 1, total: quiz.questions.length }
            }}
          </p>
        }
        @if (reviewQuestion(); as question) {
          <div class="question-card">
            <p class="question-text">{{ question.text }}</p>
            @if (quiz.materialKind === 'simple') {
              <p class="open-ended-answer-text">{{ myAnswer()?.openEndedValue }}</p>
            } @else {
              <div class="choices">
                @for (choice of reviewChoices(); track choice.value) {
                  <label
                    class="choice-row"
                    [class.choice-row--correct]="choice.value === reviewKnownCorrectChoice()"
                    [class.choice-row--incorrect]="
                      choice.value === reviewSelectedChoice() && reviewIsSelectedCorrect() === false
                    "
                    [class.choice-row--selected]="choice.value === reviewSelectedChoice()"
                  >
                    <input
                      type="radio"
                      disabled
                      [name]="'review-answer-' + question.id"
                      [checked]="choice.value === reviewSelectedChoice()"
                    />
                    <span>{{ choice.text }}</span>
                    @if (choice.value === reviewKnownCorrectChoice()) {
                      <odl-icon name="check"></odl-icon>
                    } @else if (
                      choice.value === reviewSelectedChoice() && reviewIsSelectedCorrect() === false
                    ) {
                      <odl-icon name="close"></odl-icon>
                    }
                  </label>
                }
              </div>
            }
          </div>
        }
        @if (canRetry()) {
          @if (attemptsRemaining(); as remaining) {
            <p class="attempts-remaining">
              {{ 'QUESTIONS.ATTEMPTS_REMAINING' | translate: { count: remaining } }}
            </p>
          }
        }
        <div slot="footer" class="dialog-footer" [class.dialog-footer--divider]="canRetry()">
          <div class="dialog-footer-start">
            @if (reviewIndex() > 0) {
              <odl-button variant="text" (click)="goToPreviousReviewQuestion()">
                <odl-icon name="navigate_back" slot="start"></odl-icon>
                {{ 'QUESTIONS.BACK' | translate }}
              </odl-button>
            }
          </div>
          <div class="dialog-footer-end">
            @if (!isLastReviewQuestion()) {
              <odl-button variant="primary" (click)="goToNextReviewQuestion()">
                {{ 'NEXT' | translate }}
                <odl-icon name="navigate_next" slot="end"></odl-icon>
              </odl-button>
            } @else {
              <odl-button variant="primary" (click)="close()">
                {{ 'CLOSE' | translate }}
              </odl-button>
            }
          </div>
        </div>
      }
    }
  </odl-dialog>
}
